import { isNumber, isString } from "lodash";
import { UntypedArrayId, FunctionId } from "../../../typing/typing";

import { AnyType, FunctionType, ModusPonens } from "../Types";
import { Constructors, isExternal, getExternalObjType, Reduction } from "../Lambda";
import List from "./Lists";
var C = Constructors;

function MultivariateFunctionType(n, types, tres) {
  if(types && UntypedArrayId(types).length != n) {
    throw n + "-element array expected";
  }
  var t = tres || AnyType;
  for(var i = n; --i >= 0; ) {
    var tArg = types ? types[i] : AnyType;
    t = new FunctionType(tArg, t);
  }
  return t;
}

/**
 * Constructor of multivariate functions.
 * Multivariate function accumulates arguments by unboxing External terms,
 * until `n` arguments is collected.
 * Than `f` is called with these unboxed arguments,
 * the result is boxed to External term.
 *
 * Unlike {@link EInstances.ListFunction ListFunction},
 * each argument is of its own type, these types may be different.
 * Another difference is that multivariate function accumulate its arguments
 * while list function process only one argument of List type.
 * Finally, ListFunction can be applied to a list of arbitrary length,
 * while the arity `n` of MultivariateFunction is fixed.
 *
 * @param {Function} f - JS function
 * @param {integer} n - arity (> 0)
 * @param {Type[]} [types=[AnyType, AnyType, ...]] -
 *   `n`-element array of arguments' types
 * @param {Type} [tres=AnyType] - type of result
 * @return External term with a type `t1 -> t2 -> t3 -> ... -> tn -> tres`
 * @memberof EInstances
 * @example
 * Application(
 *   Application(
 *     External(f, "MultivariateFunction", ["A", "B"], "C"),
 *     External(x, "A"),
 *   External(y, "B")
 * )) -> External(f(x, y), "C")
 */
function MultivariateFunction(f, n, types, tres) {
  if(! isNumber(n) || !(n > 0)) {
    throw "arity " + n + " is not a number > 0";
  }
  var obj = {
    func: isString(f) ? "(" + f + ")" : FunctionId(f),
    arity: n,
    arg0: [] // list of arguments
  };
  return new C.External(obj, "MultivariateFunction",
    MultivariateFunctionType(n, types, tres));
}

/**
 * Reduction of MultivariateFunction.
 * @member
 * @memberof EInstances
 */
function MultivariateFunctionReduction(term, reduce) {
  if(term instanceof C.Application &&
      isExternal(term.func, "MultivariateFunction") &&
        isExternal(term.arg)) {
    var
      obj = term.func.object,    // pair { func, arg0 }
      arg = term.arg.object,     //
      r = {
            func: obj.func,
            arity: obj.arity,
            arg0: obj.arg0.concat([arg])
          }, // result
      t = ModusPonens(term.func.type(), term.arg.type());
    if(r.arg0.length != r.arity) {
      return new C.External(r, "MultivariateFunction", t);
    }
    var res = eval(r.func).apply(this, r.arg0);
    if(t == AnyType) {
      console.log('AnyType of expression:');
      console.log(res);
    }
    return new C.External(res, getExternalObjType(t), t);
  }
}

// add to default reductions
Reduction.DefaultReductions.push(MultivariateFunctionReduction);

export default MultivariateFunction;
